/**
 * Frontmatter Writer Module - SPEC-014 AI Button Writeback
 *
 * Provides safe frontmatter manipulation for AI suggestions.
 * Only modifies ai_tags and ai_links fields, preserving all other content.
 *
 * @module utils/frontmatter-writer
 * @see SPEC-014 Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7
 */
import { parse, stringify } from 'yaml';
import { AI_TAGS_MAX, AI_LINKS_MAX } from '../schemas/ai-suggestions.zod';
// ============================================================================
// Constants
// ============================================================================
/**
 * Regex to match YAML frontmatter block.
 * Captures the YAML content between --- delimiters.
 */
const FRONTMATTER_REGEX = /^---\n([\s\S]*?)\n---\n?/;
// ============================================================================
// Errors
// ============================================================================
/**
 * Error thrown when frontmatter YAML parsing fails.
 *
 * @see SPEC-014 Requirement 12.1
 */
export class FrontmatterParseError extends Error {
    constructor(message) {
        super(message);
        this.name = 'FrontmatterParseError';
    }
}
// ============================================================================
// Helper Functions
// ============================================================================
/**
 * Extract frontmatter YAML content from markdown.
 *
 * @param markdown - Full markdown document
 * @returns YAML content string (without --- delimiters), or empty string if none
 *
 * @example
 * ```typescript
 * extractFrontmatter('---\ntitle: Test\n---\nBody') // 'title: Test'
 * extractFrontmatter('Just body') // ''
 * ```
 */
export function extractFrontmatter(markdown) {
    const match = markdown.match(FRONTMATTER_REGEX);
    return match?.[1] ?? '';
}
/**
 * Extract body content from markdown (everything after frontmatter).
 *
 * @param markdown - Full markdown document
 * @returns Body content without frontmatter
 *
 * @example
 * ```typescript
 * extractBody('---\ntitle: Test\n---\nBody content') // 'Body content'
 * extractBody('Just body') // 'Just body'
 * ```
 */
export function extractBody(markdown) {
    return markdown.replace(FRONTMATTER_REGEX, '');
}
// ============================================================================
// Main Function
// ============================================================================
/**
 * Apply AI writeback to markdown frontmatter.
 *
 * This function safely updates the frontmatter with AI-generated tags and links
 * while preserving all other frontmatter fields and the document body.
 *
 * Key behaviors:
 * - Only modifies `ai_tags` and `ai_links` fields
 * - Preserves all other frontmatter fields unchanged
 * - Preserves document body byte-for-byte
 * - Creates minimal frontmatter if none exists
 * - Enforces hard caps (8 tags, 8 links) with truncation warnings
 *
 * @param markdown - Original markdown document
 * @param writeback - AI writeback data to apply
 * @returns Result with updated markdown and any warnings
 * @throws {FrontmatterParseError} If existing frontmatter has invalid YAML
 *
 * @example
 * ```typescript
 * const result = applyWriteback('# Hello', { ai_tags: ['tag1'], ai_links: [] });
 * // result.markdown starts with frontmatter containing ai_tags
 * ```
 *
 * @see SPEC-014 Requirements 3.1, 3.2, 3.3, 3.4, 3.5, 3.6, 3.7
 */
export function applyWriteback(markdown, writeback) {
    const warnings = [];
    // Enforce hard caps (Requirement 3.5, 3.6)
    let { ai_tags, ai_links } = writeback;
    if (ai_tags.length > AI_TAGS_MAX) {
        ai_tags = ai_tags.slice(0, AI_TAGS_MAX);
        warnings.push(`ai_tags truncated to ${AI_TAGS_MAX} items`);
    }
    if (ai_links.length > AI_LINKS_MAX) {
        ai_links = ai_links.slice(0, AI_LINKS_MAX);
        warnings.push(`ai_links truncated to ${AI_LINKS_MAX} items`);
    }
    const match = markdown.match(FRONTMATTER_REGEX);
    if (match) {
        // Parse existing frontmatter (Requirement 3.4)
        const existingYaml = match[1] ?? '';
        const body = markdown.slice(match[0].length);
        let frontmatter;
        try {
            frontmatter = parse(existingYaml) || {};
        }
        catch (error) {
            throw new FrontmatterParseError(`Failed to parse existing frontmatter: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
        // Update only ai_* fields (Requirement 3.1)
        frontmatter.ai_tags = ai_tags;
        frontmatter.ai_links = ai_links;
        const newYaml = stringify(frontmatter);
        return {
            markdown: `---\n${newYaml}---\n${body}`,
            warnings,
        };
    }
    else {
        // Create minimal frontmatter (Requirement 3.3)
        const newFrontmatter = { ai_tags, ai_links };
        const newYaml = stringify(newFrontmatter);
        return {
            markdown: `---\n${newYaml}---\n${markdown}`,
            warnings,
        };
    }
}
//# sourceMappingURL=frontmatter-writer.js.map